<template>
  <!-- POC Cuidados Paliativos (Tela 3): discussão do caso entre a equipe
       assistencial e a equipe matricial, dentro do pedido de apoio. -->
  <section class="case-discussion">
    <header class="cd-header">
      <h4 class="cd-title">
        Discussão do caso
      </h4>
    </header>

    <p
      v-if="messages.length === 0"
      class="cd-empty"
    >
      Nenhuma mensagem ainda. Use este espaço para as equipes discutirem o caso.
    </p>

    <ol
      v-else
      class="cd-messages"
    >
      <li
        v-for="message in messages"
        :key="message.id"
        class="cd-message"
        :class="{ 'is-mine': message.team === team }"
      >
        <p class="cd-author">
          {{ message.authorName || "Profissional" }}
          <span class="cd-team">· {{ teamLabel(message.team) }}</span>
        </p>
        <p class="cd-text">
          {{ message.text }}
        </p>
        <p class="cd-time">
          {{ formatDate(message.createdAt) }}
        </p>
      </li>
    </ol>

    <form
      class="cd-compose"
      @submit.prevent="send"
    >
      <label
        :for="`cd-input-${request.id}`"
        class="cd-visually-hidden"
      >Mensagem para a discussão do caso</label>
      <textarea
        :id="`cd-input-${request.id}`"
        v-model="draft"
        class="cd-input"
        rows="2"
        placeholder="Escreva uma mensagem para a outra equipe"
        @keydown.enter.exact.prevent="send"
      />
      <button
        type="submit"
        class="cd-send-button"
        :disabled="!draft.trim() || sending"
      >
        Enviar
      </button>
    </form>
    <p
      v-if="error"
      class="cd-error"
      role="alert"
    >
      {{ error }}
    </p>
  </section>
</template>

<script>
import { addMatrixMessage } from "@/services/palliative/matrixSupportApi";

export default {
  name: "MatrixCaseDiscussion",
  props: {
    request: {
      type: Object,
      required: true
    },
    // "assistencial" (role 1) ou "matricial" (role 2): quem está vendo a tela.
    team: {
      type: String,
      required: true
    }
  },
  emits: ["updated"],
  data() {
    return {
      draft: "",
      sending: false,
      error: ""
    };
  },
  computed: {
    messages() {
      return this.request.messages || [];
    }
  },
  methods: {
    teamLabel(team) {
      return team === "matricial" ? "equipe matricial" : "equipe assistencial";
    },
    formatDate(iso) {
      return this.$moment(iso).format("DD/MM [às] HH:mm");
    },
    async send() {
      const text = this.draft.trim();
      if (!text || this.sending) return;

      this.sending = true;
      this.error = "";
      try {
        await addMatrixMessage(this.request.id, text);
        this.draft = "";
        this.$emit("updated");
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível enviar a mensagem.", error);
        this.error = error.message;
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cd-error {
  margin-top: 8px;
  color: #db466f;
  font-size: 12px;
}

$lila-primary: #4f3f82;
$lila-soft: #ece9f5;
$ink: #2b2b3a;
$muted: #6b6b7b;
$border: #dcd8e6;

.case-discussion {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $lila-soft;
}

.cd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.cd-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #484848;
}

.cd-video-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid $lila-primary;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: $lila-primary;
  background-color: #ffffff;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: $lila-soft;
  }
}

.cd-empty {
  margin-bottom: 12px;
  font-size: 13px;
  color: $muted;
}

.cd-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  margin: 0 0 12px;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.cd-message {
  align-self: flex-start;
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 12px 12px 12px 4px;
  background-color: #f4f4f7;

  &.is-mine {
    align-self: flex-end;
    border-radius: 12px 12px 4px 12px;
    background-color: $lila-soft;
  }
}

.cd-author {
  margin: 0 0 2px;
  font-size: 12px;
  font-weight: 700;
  color: $ink;
}

.cd-team {
  font-weight: 400;
  color: $muted;
}

.cd-text {
  margin: 0;
  font-size: 14px;
  color: $ink;
  white-space: pre-line;
}

.cd-time {
  margin: 4px 0 0;
  font-size: 11px;
  color: $muted;
  text-align: right;
}

.cd-compose {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.cd-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid $border;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: 2px solid $lila-primary;
    outline-offset: 0;
  }
}

.cd-send-button {
  padding: 8px 18px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background-color: $lila-primary;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.cd-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
